import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Typography } from 'antd';
import ChangeLogSearchCard from '../../components/body/change_logs/ChangeLogSearchCard';
import ChangeLogDiffPage from '../../components/body/change_logs/ChangeLogDiffPage';
import { fetchChangeLogsHistory, FetchChangeLogsHistoryParams } from '../../actions/change_logs';
import {
  FILTER_SEGMENT_NAME,
  FILTER_SEGMENT_TITLE,
  PARENT_PRICE_SHEET_SEGMENT_NAME,
  PARENT_PRICE_SHEET_SEGMENT_TITLE,
} from '../../constants/ChangeLogConstants';
import { ApplicationState } from '../../reducers';
import { AsyncDispatch } from '../../../types/global';
import { getPageLimit } from '../../utils/Utils';
import 'react-diff-view/style/index.css';
import { useAppDispatch } from '../../store/ReduxStore';

const { Text, Title } = Typography;

const pageSize = getPageLimit();

const ChangeLogsContainer: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();

  const { fetchingMoreChangeLogs } = useSelector((state: ApplicationState) => state.changeLogs);

  const { brands } = useSelector((state: ApplicationState) => state.parent.brands);
  const segments = useSelector(
    (state: ApplicationState) => state.resources.data.global?.segments || []
  );

  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<number[]>([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState<number[]>([]);
  const [selectedBrandCodes, setSelectedBrandCodes] = useState<string[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [selectedLogUserIds, setSelectedLogUserIds] = useState<number[]>([]);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);

  const availableSegments = useMemo(() => {
    let changeLogSegments = segments;
    changeLogSegments = changeLogSegments.filter(
      segment => segment.name !== 'flat_applications' && segment.name !== 'equipment'
    );
    const additionalAvailableSegments = [
      { id: 21, name: FILTER_SEGMENT_NAME, title: FILTER_SEGMENT_TITLE },
      { id: 22, name: PARENT_PRICE_SHEET_SEGMENT_NAME, title: PARENT_PRICE_SHEET_SEGMENT_TITLE },
    ];
    changeLogSegments = [...changeLogSegments, ...additionalAvailableSegments];
    return changeLogSegments;
  }, [segments]);

  const brandElements = useMemo(
    () =>
      brands.map(brand => ({
        id: brand.id,
        name: `${brand.name} (${brand.code})`,
        code: brand.code,
      })),
    [brands]
  );

  const handleFetchChangeLogs = useCallback(
    (params: FetchChangeLogsHistoryParams = {}) => {
      const fetchParams: FetchChangeLogsHistoryParams = {
        page: 1,
        limit: pageSize,
        ...params,
      };

      return dispatch(fetchChangeLogsHistory(fetchParams)).then((response: any) => {
        const responseData = response.value?.data || [];
        if (responseData.length < pageSize) {
          setHasMoreData(false);
        }
      });
    },
    [dispatch]
  );

  useEffect(() => {
    handleFetchChangeLogs();
  }, [handleFetchChangeLogs]);

  const handleFetchNext = useCallback(
    ({ startIndex, stopIndex }: { startIndex: number; stopIndex: number }) => {
      if (fetchingMoreChangeLogs || !hasMoreData) {
        return Promise.resolve();
      }

      const nextPage = Math.ceil(stopIndex / pageSize);
      const currentPage = Math.ceil(startIndex / pageSize);
      const lastPage = currentPage > startIndex / pageSize;

      const params: FetchChangeLogsHistoryParams = {
        page: nextPage,
        limit: pageSize,
        ...(selectedBrandIds && { brand_ids: selectedBrandIds }),
        ...(selectedItemIds && { item_ids: selectedItemIds }),
        ...(selectedSegments && { segments: selectedSegments }),
        ...(selectedLogUserIds && { log_user_ids: selectedLogUserIds }),
      };

      if (!lastPage)
        return dispatch(fetchChangeLogsHistory(params)).then((response: any) => {
          const responseData = response.value?.data || [];
          if (responseData.length < pageSize) {
            setHasMoreData(false);
          }
        });

      return Promise.resolve();
    },
    [
      fetchingMoreChangeLogs,
      hasMoreData,
      selectedBrandIds,
      selectedItemIds,
      selectedSegments,
      selectedLogUserIds,
      dispatch,
    ]
  );

  const handleSegmentChange = useCallback(
    (value: number[] | undefined) => {
      const selectedIds = value || [];
      setSelectedSegmentIds(selectedIds);
      const selectedSegmentNames = selectedIds
        .map(id => availableSegments.find(segment => segment.id === id)?.name)
        .filter((name): name is string => Boolean(name));
      setSelectedSegments(selectedSegmentNames);
    },
    [availableSegments]
  );

  const handleBrandChange = useCallback(
    (value: number[] | undefined) => {
      const selectedIds = value || [];
      setSelectedBrandIds(selectedIds);
      const selectedCodes = selectedIds
        .map(id => brandElements.find(brand => brand.id === id)?.code)
        .filter((code: string | undefined) => code !== undefined);
      setSelectedBrandCodes(selectedCodes);
      setSelectedItemIds([]);
    },
    [brandElements]
  );

  const handleItemIdsChange = useCallback((value: number[] | undefined) => {
    const selectedIds = value || [];
    setSelectedItemIds(selectedIds);
  }, []);

  const handleLogUserIdsChange = useCallback((value: number[] | undefined) => {
    const selectedUserIds = value || [];
    setSelectedLogUserIds(selectedUserIds);
  }, []);

  const handleFilterSearch = useCallback(() => {
    setHasMoreData(true);

    const params: FetchChangeLogsHistoryParams = {
      page: 1,
      limit: pageSize,
      ...(selectedBrandIds && { brand_ids: selectedBrandIds }),
      ...(selectedItemIds && { item_ids: selectedItemIds }),
      ...(selectedSegments && { segments: selectedSegments }),
      ...(selectedLogUserIds && { log_user_ids: selectedLogUserIds }),
    };

    handleFetchChangeLogs(params);
  }, [
    handleFetchChangeLogs,
    selectedBrandIds,
    selectedItemIds,
    selectedSegments,
    selectedLogUserIds,
  ]);

  return (
    <div className="change-logs-container">
      <div className="change-logs-container__header">
        <Title level={2}>{t('changeLog:title')}</Title>
        <Text type="secondary">{t('changeLog:titleDescription')}</Text>
      </div>

      <ChangeLogSearchCard
        selectedBrandCodes={selectedBrandCodes}
        selectedItemIds={selectedItemIds}
        selectedSegmentIds={selectedSegmentIds}
        selectedLogUserIds={selectedLogUserIds}
        handleBrandChange={handleBrandChange}
        handleItemIdsChange={handleItemIdsChange}
        handleSegmentChange={handleSegmentChange}
        handleLogUserIdsChange={handleLogUserIdsChange}
        handleFilterSearch={handleFilterSearch}
      />

      <ChangeLogDiffPage
        hasMoreData={hasMoreData}
        handleFetchNext={handleFetchNext}
        handleFilterSearch={handleFilterSearch}
      />
    </div>
  );
};

export default ChangeLogsContainer;
